import React, { useState } from "react";
import { Col, Input, Row } from "reactstrap";

import { ProductEnrichmentFields } from "../../../components/Catalog/CatalogStudioSharedFields";
import { getReferenceOptionLabel } from "../../../utils/catalogProductStudioShared";
import WizardIcon from "./WizardIcon";

const Collapsible = ({ t, icon, title, copy, children }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="catalog-wizard-collapsible">
      <button
        type="button"
        className="catalog-wizard-collapsible__head"
        onClick={() => setIsOpen((current) => !current)}
        aria-expanded={isOpen}
      >
        <span className="catalog-wizard-collapsible__icon">
          <WizardIcon name={icon} size={18} />
        </span>
        <span className="catalog-wizard-collapsible__text">
          <span className="catalog-wizard-collapsible__title">
            {title} <small>{t("catalogProductStudio.wizardOptional")}</small>
          </span>
          <span className="catalog-wizard-collapsible__copy">{copy}</span>
        </span>
        <WizardIcon
          name="chevronDown"
          size={20}
          className={`catalog-wizard-collapsible__chevron${isOpen ? " is-open" : ""}`}
        />
      </button>
      {isOpen ? <div className="catalog-wizard-collapsible__body">{children}</div> : null}
    </div>
  );
};

const MoneyInput = ({ name, value, onChange, invalid, id }) => (
  <div className="catalog-wizard-money">
    <span className="catalog-wizard-money__prefix" aria-hidden="true">
      €
    </span>
    <Input
      id={id}
      name={name}
      value={value}
      onChange={onChange}
      invalid={invalid}
      inputMode="decimal"
      placeholder="0.00"
    />
  </div>
);

const Required = () => (
  <span className="catalog-wizard-required" aria-hidden="true">
    *
  </span>
);

const Step1Product = ({
  t,
  core,
  errors,
  codeConflict,
  onFieldChange,
  onEnrichmentChange,
  onAddDetailSection,
  onRemoveDetailSection,
  onDetailSectionChange,
  brands,
  merchandisingTypes,
  productFamilyOptions,
  untitledLabel,
}) => {
  const handleInput = (event) => onFieldChange(event.target.name, event.target.value);

  return (
    <>
      <div className="catalog-wizard-panel__head">
        <div>
          <h2 className="catalog-wizard-panel__title">{t("catalogProductStudio.step1CardTitle")}</h2>
          <p className="catalog-wizard-panel__copy">{t("catalogProductStudio.step1CardCopy")}</p>
        </div>
      </div>

      <Row>
        <Col md="6" className="catalog-wizard-field">
          <label htmlFor="wizard-title">
            {t("catalogProductStudio.step1Name")}
            <Required />
          </label>
          <Input
            id="wizard-title"
            name="title"
            value={core.title}
            onChange={handleInput}
            invalid={Boolean(errors.title)}
            placeholder={t("catalogProductStudio.step1NamePlaceholder")}
          />
          {errors.title ? (
            <span className="catalog-wizard-error">
              {errors.title}
              {codeConflict ? (
                <>
                  {" "}
                  <a href={`/admin/catalog/products/${codeConflict.id}`} target="_blank" rel="noopener noreferrer">
                    {t("catalogProductStudio.openExistingProduct")}
                  </a>
                </>
              ) : null}
            </span>
          ) : null}
        </Col>
        <Col md="6" className="catalog-wizard-field">
          <label htmlFor="wizard-family">
            {t("catalogProductStudio.step1Category")}
            <Required />
          </label>
          <Input
            id="wizard-family"
            type="select"
            name="productFamily"
            value={core.productFamily}
            onChange={handleInput}
          >
            {productFamilyOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Input>
        </Col>
      </Row>

      <Row>
        <Col md="6" className="catalog-wizard-field">
          <label htmlFor="wizard-brand">{t("catalogProductStudio.step1Brand")}</label>
          <Input id="wizard-brand" type="select" name="brandId" value={core.brandId} onChange={handleInput}>
            <option value="">{t("catalogProductStudio.step1BrandPlaceholder")}</option>
            {brands.map((brand) => (
              <option key={brand.id} value={brand.id}>
                {getReferenceOptionLabel(brand, untitledLabel)}
              </option>
            ))}
          </Input>
        </Col>
        <Col md="6" className="catalog-wizard-field">
          <label htmlFor="wizard-type">{t("catalogProductStudio.step1Type")}</label>
          <Input
            id="wizard-type"
            type="select"
            name="merchandisingTypeId"
            value={core.merchandisingTypeId}
            onChange={handleInput}
          >
            <option value="">{t("catalogProductStudio.step1TypePlaceholder")}</option>
            {merchandisingTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {getReferenceOptionLabel(type, untitledLabel)}
              </option>
            ))}
          </Input>
          <span className="catalog-wizard-hint">{t("catalogProductStudio.step1TypeHint")}</span>
        </Col>
      </Row>

      <Row>
        <Col md="4" className="catalog-wizard-field">
          <label htmlFor="wizard-price">
            {t("catalogProductStudio.step1Price")}
            <Required />
          </label>
          <MoneyInput
            id="wizard-price"
            name="price"
            value={core.price}
            onChange={handleInput}
            invalid={Boolean(errors.price)}
          />
          {errors.price ? <span className="catalog-wizard-error">{errors.price}</span> : null}
        </Col>
        <Col md="4" className="catalog-wizard-field">
          <label htmlFor="wizard-cost">{t("catalogProductStudio.step1Cost")}</label>
          <MoneyInput id="wizard-cost" name="cost" value={core.cost} onChange={handleInput} />
          <span className="catalog-wizard-hint">{t("catalogProductStudio.step1CostHint")}</span>
        </Col>
        <Col md="4" className="catalog-wizard-field">
          <label htmlFor="wizard-compare">{t("catalogProductStudio.step1ComparePrice")}</label>
          <MoneyInput
            id="wizard-compare"
            name="compareAtPrice"
            value={core.compareAtPrice}
            onChange={handleInput}
          />
          <span className="catalog-wizard-hint">{t("catalogProductStudio.step1ComparePriceHint")}</span>
        </Col>
      </Row>

      <div className="catalog-wizard-field">
        <label htmlFor="wizard-description">{t("catalogProductStudio.step1ShortDescription")}</label>
        <Input
          id="wizard-description"
          type="textarea"
          rows="3"
          name="description"
          value={core.description}
          onChange={handleInput}
          placeholder={t("catalogProductStudio.step1DescriptionPlaceholder")}
        />
      </div>

      <label className="catalog-wizard-check">
        <input
          type="checkbox"
          name="metaCatalogEnabled"
          checked={core.metaCatalogEnabled}
          onChange={(event) => onFieldChange("metaCatalogEnabled", event.target.checked)}
        />
        <span>
          <strong>{t("catalogProductStudio.step1MetaCatalog")}</strong>
          <span>{t("catalogProductStudio.step1MetaCatalogHint")}</span>
        </span>
      </label>

      <Collapsible
        t={t}
        icon="store"
        title={t("catalogProductStudio.step1WebshopDetailsSection")}
        copy={t("catalogProductStudio.step1WebshopDetailsCopy")}
      >
        <ProductEnrichmentFields
          t={t}
          value={core.enrichment}
          onChange={onEnrichmentChange}
          showDetailSections
          onAddDetailSection={onAddDetailSection}
          onRemoveDetailSection={onRemoveDetailSection}
          onDetailSectionChange={onDetailSectionChange}
        />
      </Collapsible>

      <Collapsible
        t={t}
        icon="settings"
        title={t("catalogProductStudio.step1AdvancedSection")}
        copy={t("catalogProductStudio.step1AdvancedCopy")}
      >
        <Row>
          <Col md="6" className="catalog-wizard-field">
            <label htmlFor="wizard-base-sku">{t("catalogProductStudio.createBaseSku")}</label>
            <Input id="wizard-base-sku" name="baseSku" value={core.baseSku} onChange={handleInput} placeholder="RUNNER" />
          </Col>
          <Col md="6" className="catalog-wizard-field">
            <label htmlFor="wizard-slug">{t("catalogProductStudio.createCustomSlug")}</label>
            <Input id="wizard-slug" name="slug" value={core.slug} onChange={handleInput} placeholder="classic-runner" />
          </Col>
        </Row>
      </Collapsible>
    </>
  );
};

export default Step1Product;
